import { Component, forwardRef, Input, EventEmitter, Output, HostBinding } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { trigger, transition, animate, style } from '@angular/animations';

@Component({
  selector: 'rx-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('fadeIcon', [
      transition(':enter', [animate('.3s ease', style({ opacity: 1 }))]),
      transition(':leave', [animate('.3s ease', style({ opacity: 0 }))]),
    ]),
  ],
})
export class InputComponent implements ControlValueAccessor {
  /** Form group object */
  @Input() group?: FormGroup;

  /** Input name attribute */
  @Input() name: string;

  /** Label text */
  @Input() label?: string;

  /** Placeholder text */
  @Input() placeholder?: string;

  /** Input type attribute */
  @Input() type: string;

  /** Makes input read-only */
  @Input() readonly?: boolean;

  /** Set value manually */
  @Input() value?: string;

  /** Input autocomplete attribute */
  @Input() autocomplete?: any;

  /** Help text below input */
  @Input() help?: string;

  /** Secondary label text */
  @Input() secondaryLabel?: string;

  /** Icon on the left side */
  @Input() leftIcon?: string;

  /** Iocn on the right side */
  @Input() rightIcon?: string;

  /** Icon for cleaning up / removing the input text and value */
  @Input() cleanFieldIcon = false;

  /** Max length of input text */
  @Input() maxLength?: number;
  @Input() minLength?: number;

  /** Trigger validation when the blur event occurs by changing the 'touched' property of the FormControl to true */
  @Input() validateOnBlur = false;

  /** On input toggle focus */
  @Output() toggle = new EventEmitter<any>();

  /** On clean field */
  @Output() cleanField = new EventEmitter<any>();

  /** Compact version of input */
  @HostBinding('class.compact') @Input() compact: boolean;

  /** Disable input */
  @HostBinding('class.disabled') @Input() disabled?: boolean;

  /** Error text */
  @HostBinding('class.invalid') @Input() error: string;

  /** Is input focused state */
  public isFocus = false;

  public onChange = (value: any) => {
    /* Part of NG_VALUE_ACCESSOR implementation */
  };
  public onTouched = () => {
    /* Part of NG_VALUE_ACCESSOR implementation */
  };
  public registerOnTouched = (fn: any) => {
    this.onTouched = fn;
  };
  public writeValue = (value: any) => {
    this.value = value;
  };
  public registerOnChange = (fn: (val: any) => void) => (this.onChange = fn);
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Focus on input field */
  public onFocus(event: FocusEvent) {
    this.isFocus = true;
    this.toggle.emit(event);
  }

  /** Un-focus of input field */
  public onBlur(event: FocusEvent) {
    if(this.validateOnBlur) {
      this.onTouched();
    }
    this.isFocus = false;
    this.toggle.emit(event);
  }

  /** Clean input text /value */
  public clickCleanIcon() {
    this.value = '';
    this.writeValue('');
    this.cleanField.emit();
  }
}
